import { Component, OnDestroy, OnInit } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

@Component({
  selector: 'ngx-ellipsis-cell',
  template: `
    <div class="truncate"
         (mouseenter)="mouseenter()"
         (mouseleave)="mouseleave()"
         [ngStyle]="customStyle">
      {{ display }}
       <span *ngIf="addData" [ngStyle]="addDataStyle">{{ addData }}</span>
    </div>
  `,
  styles: [`.truncate{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}`]
})
export class EllipsisCellComponent implements ICellRendererAngularComp, OnInit, OnDestroy {

  constructor() { }

  params: any;
  display = '';
  tooltipText: string | null = null;
  clickHintText: string | null = null;
  private customTooltipEl: HTMLDivElement | null = null;
  customStyle: any = { 'text-align': 'center' };

  header: any;
  rowData: any;
  addData: string | null = null;
  addDataStyle: any = null;

  agInit(params: any) {
    this.params = params;
    this.display = this.getDisplay(params);
    this.tooltipText = (typeof params.value === 'object' && params.value?.tooltip) ? String(params.value.tooltip) : null;
    this.clickHintText = (typeof params.value === 'object' && params.value?.clickHint) ? String(params.value.clickHint) : null;
    if (params.value.fontWeight) {
      this.customStyle.fontWeight = params.value.fontWeight;
    }
    if (params.value.textAlign) {
      this.customStyle['text-align'] = params.value.textAlign;
    }
  }

  getDisplay(params: any) {
    let value = typeof params.value === 'object' ? String(params.value?.value ?? '') : String(params.valueFormatted ?? params.value ?? '');
    if (params?.value?.numFmt === '#,##0') {
      // add commas to number
      value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    if (params?.value?.addData) {
      if (!params.value.addDataStyle) {
        value += params.value.addData;
      } else {
        this.addData = params.value.addData;
        this.addDataStyle = params.value.addDataStyle;
      }
    }
    return value;
  }

  refresh(p: any) {
    this.display = this.getDisplay(p);
    this.params = p;
    this.header = p.header;
    this.rowData = p.data;
    this.tooltipText = (typeof p.value === 'object' && p.value?.tooltip) ? String(p.value.tooltip) : null;
    this.clickHintText = (typeof p.value === 'object' && p.value?.clickHint) ? String(p.value.clickHint) : null;
    return true;
  }

  mouseenter() {
    const text = this.getTooltipText();
    // An empty clickable cell still shows its click hint.
    if (text || this.clickHintText) this.showCustomTooltip(text ?? '');
  }

  mouseleave() {
    this.hideCustomTooltip();
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.hideCustomTooltip();
  }

  private getTooltipText(): string | null {
    if (this.tooltipText) return this.tooltipText;
    if (this.clickHintText) return this.display;
    // overflow-only: show full text when truncated
    const el = (this.params as any).eGridCell?.querySelector('.truncate') as HTMLElement;
    if (el && el.scrollWidth > el.clientWidth && this.display) return this.display;
    return null;
  }

  private showCustomTooltip(text: string): void {
    if (this.customTooltipEl) return;
    const cellEl = (this.params as any).eGridCell as HTMLElement;
    if (!cellEl) return;
    const rect = cellEl.getBoundingClientRect();

    const divider = text ? '<div style="margin: 8px -1rem; border-top: 1px solid rgba(143, 155, 179, 0.5);"></div>' : '';
    const hintHtml = this.clickHintText
      ? `${divider}
         <div style="font-weight: 700;">${escapeHtml(this.clickHintText)}</div>`
      : '';

    const el = document.createElement('div');
    el.className = 'ellipsis-click-hint-tooltip';
    el.innerHTML = `
      <div class="ech-content" style="
        color: #222b45;
        font-family: 'Open Sans', sans-serif;
        font-size: 0.75rem;
        font-weight: 400;
        line-height: 1rem;
        white-space: pre-line;
        word-break: break-word;
        text-align: ${text.includes('\n') ? 'left' : 'center'};
      ">
        ${text ? `<div>${escapeHtml(text)}</div>` : ''}
        ${hintHtml}
      </div>
      <div class="ech-arrow" style="
        position: absolute;
        bottom: -7px;
        left: 50%;
        margin-left: -7px;
        width: 0;
        height: 0;
        border-left: 7px solid transparent;
        border-right: 7px solid transparent;
        border-top: 7px solid #edf1f7;
        filter: drop-shadow(0 2px 1px rgba(44, 51, 73, 0.12));
      "></div>
    `;
    Object.assign(el.style, {
      position: 'fixed',
      top: `${Math.max(rect.top - 10, 4)}px`,
      left: `${rect.left + rect.width / 2}px`,
      transform: 'translate(-50%, -100%)',
      background: '#edf1f7',
      border: '1px solid rgba(143, 155, 179, 0.4)',
      padding: '0.5rem 1rem',
      borderRadius: '0.25rem',
      maxWidth: '16rem',
      zIndex: '10000',
      pointerEvents: 'none',
      boxShadow: '0 8px 20px rgba(44, 51, 73, 0.18), 0 2px 4px rgba(44, 51, 73, 0.08)',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(el);
    this.customTooltipEl = el;
  }

  private hideCustomTooltip(): void {
    if (this.customTooltipEl) {
      this.customTooltipEl.remove();
      this.customTooltipEl = null;
    }
  }

}
